<script setup lang="ts">
import { calMappings } from "~/composables/useCalCom";
import "vue3-toastify/dist/index.css";

const route = useRoute();
const router = useRouter();
const { openModal } = useCalCom();

watch(
  () => route.query.cal,
  (newVal) => {
    if (newVal === "open") {
      const type = route.query.type as string;
      const customUrl = route.query.url as string;

      // Prioritize mapping if 'type' is provided, then fallback to 'url'
      const targetUrl =
        calMappings[type] ||
        (typeof customUrl === "string" ? customUrl : undefined);

      openModal(targetUrl);

      // Clear query params to avoid re-opening on navigation/refresh
      router.replace({
        query: {
          ...route.query,
          cal: undefined,
          type: undefined,
          url: undefined,
        },
        hash: route.hash,
      });
    }
  },
  { immediate: true },
);

onMounted(() => {
  const elements = document.querySelectorAll('[class*="intersect:"]');

  if (elements.length === 0) {
    console.warn("No elements with intersect: found");
    return;
  }

  const { trackSectionView } = useAnalytics();
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        const el = entry.target as HTMLElement;
        const { isIntersecting } = entry;

        if (isIntersecting) {
          el.removeAttribute("no-intersect");

          el.style.animation = "none";
          void el.offsetHeight; // Trigger reflow
          el.style.animation = "";

          // Track section view if element has an ID
          if (el.id) {
            trackSectionView(el.id);
          }

          if (el.classList.contains("intersect-once")) {
            obs.unobserve(el);
          }
        } else {
          el.setAttribute("no-intersect", "");
        }
      });
    },
    {
      threshold: 0.2, // Trigger when 20% of section is visible
    },
  );

  // Observe all matching elements (e.g., sections with IDs or animate targets)
  const sections = document.querySelectorAll(
    'section[id], [class*="intersect:"]',
  );
  sections.forEach((el) => observer.observe(el));
});

useSeoMeta({
  title: "SpendZen",
  titleTemplate: (title) => (title ? `${title} | SpendZen` : "SpendZen - Simplifiez vos notes de frais"),
  ogTitle: "SpendZen",
  description:
    "Gérez vos notes de frais en toute sérénité avec SpendZen. Capturez vos reçus, laissez l'IA extraire les données et exportez vos rapports en quelques clics.",
  ogDescription:
    "Gérez vos notes de frais en toute sérénité avec SpendZen. Capturez vos reçus, laissez l'IA extraire les données et exportez vos rapports en quelques clics.",
  ogImage: "https://spendzen.ch/images/ogCover.png",
  ogImageAlt: "SpendZen",
  twitterCard: "summary_large_image",
  ogUrl: "https://spendzen.ch",
  keywords:
    "notes de frais, gestion de frais, spendzen, frais professionnels, scan reçu, extraction ia, comptabilité, pme, freelance, entreprise",
});
</script>
<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>

<style>
html {
  font-family: Geologica, sans-serif;
  scroll-padding-top: 150px;
}

button {
  font-family: Geologica, sans-serif !important;
}

.gradient-text {
  background: linear-gradient(135deg, #00a0a0 0%, #007f80 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.gradient-text-light {
  background: linear-gradient(135deg, #ffffff 0%, #b8ffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

body {
  margin: 0;
}

/* Page transitions disabled - were causing rendering issues
.page-enter-active,
.page-leave-active {
  transition: all 0.4s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}
*/
</style>
